import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { RadioGroup, RadioButton, Icon, Textarea } from '@cbrebuild/blocks';
import { useDispatch, useSelector } from 'react-redux';
import VoucherComments from '../voucher/VoucherComments';
import VoucherFilesContainer from './files/voucher-files-container';
import { updateDeal } from '../../redux/actions/deal-actions';
import {
  fetchOfficeTypes,
  updateVoucher,
} from '../../redux/actions/voucher/voucher-actions';
import { fetchVoucherFiles, fetchDealOnlyFiles } from '../../redux/actions/deal-files-actions';
import CommissionAttachmentSelector from '../../page-templates/voucher/components/CommissionAttachmentSelector';
import useVoucherValidation from './useVoucherValidation';
import BottomNav from './BottomNav';
import useFormState from '../../hooks/useFormState';
import Tooltip from '../../nucleus/tooltip/tooltip';
import Select from '../../nucleus/select/select';
import ClientCodeAutocomplete from '../autocomplete/client-code-autocomplete';
import {
  updateAttachment,
} from '../../services/attachments-service';
import verificationService from '../../services/verification-service';

const sourceByOptions = [
  { value: 'Office', display_name: 'Office' },
  { value: 'Int Office', display_name: 'Int Office' },
  { value: 'Affiliate', display_name: 'Affiliate' },
  { value: 'GCS', display_name: 'GCS' },
];

const COMMISSION_AGREEMENT_CATEGORY = 'CA';
const OTHER_ATTACHMENT_CATEGORY = 'OTH';

// Files are only tagged as a commission agreement when that question has been answered Yes.
const getDefaultAttachmentCategory = (hasCommissionAgreement) => {
  if (hasCommissionAgreement === true) return COMMISSION_AGREEMENT_CATEGORY;
  if (hasCommissionAgreement === false) return OTHER_ATTACHMENT_CATEGORY;
  return null;
};

const PAGE_NUMBER = 1.1;

const GettingStarted = ({ location }) => {
  const {
    deal, voucher, offices, voucherFiles, brokerVerificationFlagOn,
  } = useSelector(state => ({
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
    voucherFiles: state.dealFiles?.voucherFiles?.files || [],
    brokerVerificationFlagOn: !!state.userOptions?.userOptions?.feature_flags?.broker_verification_flag,
    offices: state.voucher.officeTypes.map(o => ({
      display_name: o.managing_office_name,
      value: o.managing_office_name,
    })),
  }));
  const dispatch = useDispatch();
  const accountClientTooltip = (
    <span>
      An account is a corporate occupier client relationship, that may be governed by a
      Master Service Agreement (MSA), spanning multiple markets and sustained over multiple
      years to deliver real estate advisory services across the client&apos;s portfolio.
      <br /><br />
      If the account value does not exist in this list, please request a new PeopleSoft
      client value through your established process, then proceed with this voucher.
    </span>
  );
  const initClient = deal.account_code && deal.account_description
    ? `${deal.account_code} - ${deal.account_description}` : '';
  const [clientInitSearchTerm, setClientInitSearchTerm] = useState(initClient);
  const [commissionAgreementFiles, setCommissionAgreementFiles] = useState([]);
  const [commissionAttachmentSelection, setCommissionAttachmentSelection] = useState(null);
  const [commissionAttachmentDisplaySelection, setCommissionAttachmentDisplaySelection] = useState(null);
  const [verificationSaveError, setVerificationSaveError] = useState('');
  // Bridges the gap between an upload's onUpload and onChange callbacks, which fire
  // synchronously before commissionAgreementFiles' refetch has updated component state.
  const uploadedFileCacheRef = useRef({});
  const availableCommissionAgreementFiles = useMemo(() => (
    [...new Map(
      [...voucherFiles, ...commissionAgreementFiles]
        .map(file => [String(file.id), file])
    ).values()]
  ), [commissionAgreementFiles, voucherFiles]);
  // Auto-selection tracks whichever category matches the current Yes/No answer,
  // so switching the answer surfaces that answer's previously-tagged files, not just CA ones.
  const defaultAttachmentCategory = getDefaultAttachmentCategory(deal.has_commission_agreement);
  const commissionAttachmentIds = commissionAttachmentSelection?.dealId === deal.id
    && commissionAttachmentSelection?.hasCommissionAgreement === deal.has_commission_agreement
    ? commissionAttachmentSelection.ids
    : availableCommissionAgreementFiles
      .filter(file => (
        file.document_category === defaultAttachmentCategory
        || (deal.has_commission_agreement === true && file.is_commission_agreement_category === true)
      ))
      .map(file => file.id);
  const commissionAttachmentDisplayIds = commissionAttachmentDisplaySelection?.dealId === deal.id
    && commissionAttachmentDisplaySelection?.hasCommissionAgreement === deal.has_commission_agreement
    ? commissionAttachmentDisplaySelection.ids
    : commissionAttachmentIds;
  const displayedCommissionAttachmentIds = new Set(
    commissionAttachmentDisplayIds
      .map(id => parseInt(id, 10))
      .filter(Number.isFinite)
      .map(String)
  );
  const selectedCommissionAgreementFiles = availableCommissionAgreementFiles.filter(file => (
    displayedCommissionAttachmentIds.has(String(file.id))
  ));

  const handleCommissionAttachmentChange = (
    attachmentIds,
    newAttachmentCategory = getDefaultAttachmentCategory(deal.has_commission_agreement),
  ) => {
    const selectedIds = (attachmentIds || []).map(id => parseInt(id, 10)).filter(Number.isFinite);
    const selectedIdSet = new Set(selectedIds);
    const currentIdSet = new Set(
      commissionAttachmentIds.map(id => parseInt(id, 10)).filter(Number.isFinite)
    );
    const changedCategories = [
      ...selectedIds
        .filter(id => !currentIdSet.has(id))
        .map(id => [id, newAttachmentCategory]),
      ...[...currentIdSet]
        .filter(id => !selectedIdSet.has(id))
        .map(id => [id, null]),
    ];

    const displayedIds = new Set(
      commissionAttachmentDisplayIds.map(id => parseInt(id, 10)).filter(Number.isFinite)
    );
    selectedIds.forEach(id => displayedIds.add(id));

    setCommissionAttachmentSelection({ dealId: deal.id, hasCommissionAgreement: deal.has_commission_agreement, ids: selectedIds });
    setCommissionAttachmentDisplaySelection({ dealId: deal.id, hasCommissionAgreement: deal.has_commission_agreement, ids: [...displayedIds] });
    const buildAttachmentUpdates = files => changedCategories.map(([attachmentId, attachmentCategory]) => {
      const file = files.find(f => parseInt(f.id, 10) === attachmentId)
        || uploadedFileCacheRef.current[attachmentId];
      return file ? { attachmentId, attachmentCategory, file } : null;
    });
    const saveAttachmentUpdates = (attachmentUpdates) => {
      if (attachmentUpdates.some(update => !update)) {
        setVerificationSaveError('Unable to update commission agreement selection. Please try again.');
        return Promise.resolve();
      }

      return Promise.all(attachmentUpdates.map(({ attachmentId, attachmentCategory, file }) => (
        updateAttachment(attachmentId, {
          ...file,
          is_lease_document: true,
          document_category: attachmentCategory,
        })
      )))
        .then(() => {
          setVerificationSaveError('');
          const savedById = new Map(attachmentUpdates.map(update => [String(update.attachmentId), update.attachmentCategory]));
          setCommissionAgreementFiles(prev => prev.map(file => (savedById.has(String(file.id))
            ? { ...file, is_lease_document: true, document_category: savedById.get(String(file.id)) }
            : file)));
          // Same refresh as Add to Voucher: the sections' lists and counts only catch up from the server.
          const params = {
            ordering: '-modified', page: 1, page_size: 25, deal: deal.id,
          };
          dispatch(fetchVoucherFiles(params));
          dispatch(fetchDealOnlyFiles(params));
        })
        .catch(() => {
          setVerificationSaveError('Unable to update commission agreement selection. Please try again.');
        });
    };

    const attachmentUpdates = buildAttachmentUpdates(availableCommissionAgreementFiles);
    if (attachmentUpdates.some(update => !update)) {
      verificationService.fetchVerificationAttachments(deal.id)
        .then((fetchedFiles) => {
          const files = Array.isArray(fetchedFiles) ? fetchedFiles : [];
          setCommissionAgreementFiles(prev => [...new Map(
            [...prev, ...files].map(file => [String(file.id), file]),
          ).values()]);
          return saveAttachmentUpdates(buildAttachmentUpdates([
            ...availableCommissionAgreementFiles,
            ...files,
          ]));
        })
        .catch(() => {
          setVerificationSaveError('Unable to update commission agreement selection. Please try again.');
        });
      return;
    }

    saveAttachmentUpdates(attachmentUpdates);
  };

  const handleVoucherAttachmentRemoved = (attachmentId) => {
    const removedId = parseInt(attachmentId, 10);
    setCommissionAttachmentSelection({
      dealId: deal.id,
      hasCommissionAgreement: deal.has_commission_agreement,
      ids: commissionAttachmentIds.filter(id => parseInt(id, 10) !== removedId),
    });
    setCommissionAttachmentDisplaySelection({
      dealId: deal.id,
      hasCommissionAgreement: deal.has_commission_agreement,
      ids: commissionAttachmentDisplayIds.filter(id => parseInt(id, 10) !== removedId),
    });
  };

  const {
    is_referral_deal, is_confidential, is_dual_rep_deal, voucher_lead_office, sourced_by, voucher_office_name,
    onStateChange: onDealChange, onStateChanged: onDealChanged,
  } = useFormState({
    is_referral_deal: deal.is_referral_deal,
    is_confidential: deal.is_confidential,
    is_dual_rep_deal: deal.is_dual_rep_deal,
    is_fully_executed_lease: deal.is_fully_executed_lease,
    has_commission_agreement: deal.has_commission_agreement,
    sourced_by: deal.sourced_by,
    voucher_lead_office: deal.voucher_lead_office || '',
    voucher_office_name: deal.voucher_office_name || '',
    reason_for_early_invoice: deal.reason_for_early_invoice || '',
  }, (update) => { dispatch(updateDeal(deal.id, update)); });

  const {
    reason_for_early_invoice, onStateChange: onVoucherChange, onStateChanged: onVoucherChanged,
  } = useFormState({
    reason_for_early_invoice: voucher.reason_for_early_invoice,
  }, update => dispatch(updateVoucher(voucher.id, update)));

  // uses the useValidation hook to see if this page should show all validation
  const { showPageValidation } = useVoucherValidation(voucher.id, PAGE_NUMBER);

  // fetches offices on load for lead office dropdown
  useEffect(() => {
    dispatch(fetchOfficeTypes());
  }, [dispatch]);

  // is_account_management_client intentionally has no default — the field is required
  // and the user must explicitly select Yes or No. Leaving it null shows the required error.

  const handleSourceByChange = (event) => {
    if (event.value !== 'Select') {
      dispatch(updateDeal(deal.id, { sourced_by: event.value }));
    }
  };

  const handleLeadOffice = (event) => {
    if (event.value !== 'Select') {
      dispatch(updateDeal(deal.id, { voucher_lead_office: event.value }));
    }
  };

  const handleAccountManagementClientChange = (event) => {
    const isClient = event.target.value === 'true';
    if (!isClient) {
      setClientInitSearchTerm('');
      dispatch(updateDeal(deal.id, {
        is_account_management_client: false,
        account_code: 'NONCL',
        account_description: 'Non Client',
      }));
    } else {
      setClientInitSearchTerm('');
      dispatch(updateDeal(deal.id, {
        is_account_management_client: true,
        account_code: null,
        account_description: null,
      }));
    }
  };

  const handleClientSelected = (category) => {
    if (!category || typeof category === 'string') {
      const text = category || '';
      setClientInitSearchTerm(text);
      // User cleared the field — persist the clear to backend
      if (!text && (deal.account_code || deal.account_description)) {
        dispatch(updateDeal(deal.id, {
          account_code: null,
          account_description: null,
        }));
      }
      return;
    }
    setClientInitSearchTerm(`${category.code} - ${category.description}`);
    dispatch(updateDeal(deal.id, {
      account_code: category.code,
      account_description: category.description,
    }));
  };

  // memoizes error states as input changes
  const validVoucherOfficeName = useMemo(() => !(voucher_office_name.value === '' || voucher_office_name.value === null), [voucher_office_name]);
  const validReasonForEarlyInvoice = useMemo(() => (
    !(reason_for_early_invoice.value === '' || reason_for_early_invoice.value === null)), [reason_for_early_invoice]);

  // since we use these variable 3 times each below, setting a variable instead
  const officeNameError = (showPageValidation || voucher_office_name.dirty) && !validVoucherOfficeName;
  const reasonForEarlyInvoiceError = (showPageValidation || reason_for_early_invoice.dirty) && !validReasonForEarlyInvoice;
  const accountMgmtClientError = showPageValidation && (deal.is_account_management_client === null || deal.is_account_management_client === undefined);
  const clientCodeError = showPageValidation && deal.is_account_management_client === true && !deal.account_code;
  const welcomeMessage = dealType => (
    dealType === 'lease' || dealType === 'sale' ?
      <>
        <h2>Begin {dealType.charAt(0).toUpperCase() + dealType.slice(1)} Voucher</h2>
      </> :
      <>
        <h2>Begin Miscellaneous Voucher</h2>
      </>
  );

  return (
    <React.Fragment>
      <div className="voucher-content">
        {voucher.is_early_invoice ?
          <span>
            <h2>Request Early Invoice</h2>
            <p>To request an early invoice, complete the Early Invoice Request by entering all required fields.
              Once complete, enter your Business Operations individual/group email address to the recipient list
              and select the Send button. Your Business Operations team will then process your early invoice request.
              For additional information, please reference the
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://cbre.box.com/s/q5ivrwqvt8ky6134ffsjylre9cm09mra"
                className="md-mbBroker-theme"
              > Early Invoice Process Job Aid.
              </a>
            </p>
            <label><span className={`${reasonForEarlyInvoiceError ? 'asterisk-error' : ''}`}>* </span>Reason for requesting an Early Invoice</label>
            <Textarea
              name="reason_for_early_invoice"
              className={`${reasonForEarlyInvoiceError ? 'errorInput ' : ''}invoiceArea`}
              onChange={onVoucherChange}
              value={reason_for_early_invoice.value || ''}
              rows={{ min: 4, max: 130 }}
              onBlur={onVoucherChanged}
            />
            {reasonForEarlyInvoiceError && <p className="asterisk-error errorMsg">Reason for requesting an Early Invoice is required</p>}
            <legend className="begin-voucher">Attach Files to Early Invoice</legend>
            <p>Attach any related documents for this early invoice.</p>
          </span>
          : welcomeMessage(deal.deal_type)
        }
        <div className="voucher-file-upload-content">
          <VoucherFilesContainer
            voucher={voucher}
            dealId={deal.id}
            onVoucherFileRemoved={handleVoucherAttachmentRemoved}
            commissionAgreementFiles={selectedCommissionAgreementFiles}
            commissionAgreementFileIds={selectedCommissionAgreementFiles.map(file => file.id)}
            commissionAgreementDealOnlyCount={selectedCommissionAgreementFiles
              .filter(file => file.is_lease_document === false).length}
            showImportBanner
            showGettingStartedLayout
            showAttachFilesIntro={!voucher.is_early_invoice}
          />
        </div>
        {brokerVerificationFlagOn && (
          <div className="getting-started-verification">
            <legend className="secondary-legend">
              <span className={showPageValidation && deal.is_fully_executed_lease == null ? 'asterisk-error' : ''}>* </span>
              Is this a fully executed lease?
            </legend>
            <RadioGroup
              name="is_fully_executed_lease"
              selectedValue={deal.is_fully_executed_lease == null ? '' : `${deal.is_fully_executed_lease}`}
              className="radioVal"
              disabled={false}
              onChange={onDealChanged}
              orientation="vertical"
            >
              <RadioButton value="false">No</RadioButton>
              <RadioButton value="true">Yes</RadioButton>
            </RadioGroup>
            {showPageValidation && deal.is_fully_executed_lease == null && (
              <p className="asterisk-error error-text">Fully executed lease is required</p>
            )}
            <legend className="secondary-legend">
              <span className={showPageValidation && deal.has_commission_agreement == null ? 'asterisk-error' : ''}>* </span>
              Is there a commission agreement?
            </legend>
            <RadioGroup
              name="has_commission_agreement"
              selectedValue={deal.has_commission_agreement == null ? '' : `${deal.has_commission_agreement}`}
              className="radioVal"
              disabled={false}
              onChange={onDealChanged}
              orientation="vertical"
            >
              <RadioButton value="false">No</RadioButton>
              <RadioButton value="true">Yes</RadioButton>
            </RadioGroup>
            {showPageValidation && deal.has_commission_agreement == null && (
              <p className="asterisk-error error-text">Commission agreement is required</p>
            )}
            {deal.has_commission_agreement !== null && deal.has_commission_agreement !== undefined && (
              <CommissionAttachmentSelector
                dealId={deal.id}
                selectedIds={commissionAttachmentIds}
                voucherFiles={voucherFiles}
                // Files uploaded here always get document_category CA/OTH, so they're lease documents.
                isVoucherAttachment
                autoSelectUploadedFile
                onUpload={(createdAttachment) => {
                  if (!createdAttachment?.id) return;
                  const createdId = parseInt(createdAttachment.id, 10);
                  uploadedFileCacheRef.current[createdId] = createdAttachment;
                  // Upload response already has the full record -- no need to refetch the list.
                  setCommissionAgreementFiles(prev => [
                    createdAttachment,
                    ...prev.filter(file => parseInt(file.id, 10) !== createdId),
                  ]);
                }}
                onChange={handleCommissionAttachmentChange}
                isRequired={deal.has_commission_agreement === true}
                uploadTitle={deal.has_commission_agreement === true ? "If yes, upload a commission agreement (required)." : "Upload Supporting Documentation."}
                allowEmailFiles
                showSelectedFiles={false}
              />
            )}
            {verificationSaveError && <p className="asterisk-error" role="alert">{verificationSaveError}</p>}
          </div>
        )}
        <div className="getting-started-details">
          <legend>Details</legend>
          <legend className="secondary-legend">Is this deal Confidential?</legend>
          <RadioGroup
            name="is_confidential"
            selectedValue={`${is_confidential.value}`}
            className="radioVal"
            onChange={onDealChanged}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes - Do not send to Voucher Search or VOL.</RadioButton>
          </RadioGroup>
          <legend className="secondary-legend">Is this a Referral Deal?</legend>
          <RadioGroup
            name="is_referral_deal"
            className="radioVal"
            selectedValue={`${is_referral_deal.value}`}
            onChange={onDealChanged}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
          <legend className="secondary-legend">
            Rep Role: <strong>{deal.rep_role ? `${deal.rep_role.charAt(0).toUpperCase()}${deal.rep_role.slice(1)}` : ''}</strong>
          </legend>
          <div className="radioVal">Did CBRE represent both sides of this deal?
            <Tooltip message="
              These are only deals where CBRE represented both sides of the deal. This indicates to Accounting whether to expect two vouchers or not."
            >
              <Icon iconName="info-circle" />
            </Tooltip>
          </div>
          <RadioGroup
            name="is_dual_rep_deal"
            className="radioVal"
            selectedValue={`${is_dual_rep_deal.value}`}
            onChange={onDealChanged}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
          <div className="field drop-downs">
            <label>
              <span className={(voucher_lead_office.value === '' || voucher_lead_office.value === 'Select') ?
              'asterisk-error' : ''}
              >*
              </span>Lead Office
            </label>
            <Select
              className={(voucher_lead_office.value === '' || voucher_lead_office.value === 'Select') ? 'errorInput' : ''}
              defaultOption={voucher_lead_office.value || 'Select'}
              options={offices.filter(opt => opt.value !== '')}
              onChange={handleLeadOffice}
            />
            { (voucher_lead_office.value === '' || voucher_lead_office.value === 'Select') &&
            <p className="asterisk-error error-text">Lead Office is required</p>}
          </div>
          <div className="field drop-downs">
            <label>
              Sourced By
            </label>
            <Select
              defaultOption={sourced_by.value || 'Select'}
              options={sourceByOptions.filter(opt => opt.value !== '')}
              onChange={handleSourceByChange}
            />
          </div>
          <div className="field">
            <label><span className={`${officeNameError ? 'asterisk-error' : ''}`}>* </span> Office Name</label>
            <input
              type="text"
              name="voucher_office_name"
              className={`${officeNameError ? 'errorInput' : ''}`}
              value={voucher_office_name.value}
              onChange={onDealChange}
              onBlur={onDealChanged}
            />
            {officeNameError ? <p className="asterisk-error">Office Name is required</p> : ''}
          </div>

          <div className="field">
            <legend className="secondary-legend">
              <span className={accountMgmtClientError ? 'asterisk-error' : ''}>* </span>
              Is this an Account Client?
              <Tooltip message={accountClientTooltip}>
                <Icon iconName="info-circle" />
              </Tooltip>
            </legend>
            <RadioGroup
              name="is_account_management_client"
              selectedValue={deal.is_account_management_client == null
                ? '' : `${deal.is_account_management_client}`}
              className="radioVal"
              onChange={handleAccountManagementClientChange}
              orientation="vertical"
            >
              <RadioButton value="false">No</RadioButton>
              <RadioButton value="true">Yes</RadioButton>
            </RadioGroup>
            {accountMgmtClientError && <p className="asterisk-error error-text">Account Client is required</p>}
          </div>

          {deal.is_account_management_client === true && (
            <div className="field drop-downs">
              <label>
                <span className={clientCodeError ? 'asterisk-error' : ''}>* </span>
                Client Code - Description
              </label>
              <ClientCodeAutocomplete
                initSearchTerm={clientInitSearchTerm}
                onChange={handleClientSelected}
                errorMessage={clientCodeError ? 'You must select Client Code - Description from search results' : ''}
                placeholder="Type to filter client codes"
              />
            </div>
          )}

          <BottomNav location={location.pathname} />
        </div>
      </div>
      <VoucherComments />
    </React.Fragment>
  );
};

GettingStarted.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default GettingStarted;
